// SLA Configuration Service (Global API)
import {
  CustomerSlaConfigurationPayload,
  SLAConfiguration,
  SLAConfigurationList,
  SLAConfigurationPayload,
  SLAHistory,
} from "@/utils/types/slaConfiguration";
import { get, post, patch, del } from "@/services/axiosUitils";

// Updated base path based on the user's curl command
const basePath = "/sla/service-level-agreements/";
const customerSlaConfigurationsPath = "/customer-sla/sla-configurations/";

class SLAConfigurationService {
  /**
   * Get all SLA configurations with pagination and search
   */
  getAllConfigurations = async (params?: {
    page?: number;
    pageSize?: number;
    search?: string;
  }): Promise<SLAConfigurationList> => {
    const queryParams: any = {};
    if (params?.page) {
      queryParams.page = params.page;
    }
    if (params?.pageSize) {
      queryParams.page_size = params.pageSize;
    }
    if (params?.search) {
      queryParams.search = params.search;
    }

    return await get(basePath, { params: queryParams });
  };

  /**
   * Get SLA configuration by ID
   */
  getConfigurationById = async (id: string): Promise<SLAConfiguration> => {
    return await get(`${basePath}${id}/`);
  };

  /**
   * Create new SLA configuration
   */
  createConfiguration = async (
    payload: SLAConfigurationPayload
  ): Promise<SLAConfiguration> => {
    return await post(basePath, payload);
  };

  /**
   * Update existing SLA configuration
   */
  updateConfiguration = async (
    id: string,
    payload: SLAConfigurationPayload
  ): Promise<SLAConfiguration> => {
    return await patch(`${basePath}${id}/`, payload);
  };

  /**
   * Delete SLA configuration
   */
  deleteConfiguration = async (id: string): Promise<void> => {
    return await del(`${basePath}${id}/`);
  };

  /**
   * Get SLA history for a record
   */
  getSLAHistory = async (
    recordType: string,
    recordId: string
  ): Promise<SLAHistory> => {
    return await get(`/caseline/sla/history/`, {
      params: { record_type: recordType, record_id: recordId },
    });
  };

  /**
   * Get all holiday lists
   */
  getAllHolidayLists = async (): Promise<any[]> => {
    return await get("/sla/holiday-lists/");
  };

  /**
   * Create new holiday list
   */
  createHolidayList = async (payload: any): Promise<any> => {
    return await post("/sla/holiday-lists/", payload);
  };

  /**
   * Get business calendars for dropdown
   */
  getBusinessCalendars = async (): Promise<any[]> => {
    const data = await get("/sla/holiday-lists/"); // Mapping to holiday lists
    // Adapt response if needed
    if (data && Array.isArray(data)) {
      return data.map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    } else if (data?.results && Array.isArray(data.results)) {
      return data.results.map((item: any) => ({
        label: item.name,
        value: item.id,
      }));
    }
    return [];
  };

  /**
   * Get timezones for dropdown
   */
  getTimezones = async (): Promise<any[]> => {
    return [
      { label: "America/New_York", value: "America/New_York" },
      { label: "America/Chicago", value: "America/Chicago" },
      { label: "America/Los_Angeles", value: "America/Los_Angeles" },
      { label: "Europe/London", value: "Europe/London" },
      { label: "Asia/Kolkata", value: "Asia/Kolkata" },
      { label: "Asia/Tokyo", value: "Asia/Tokyo" },
      { label: "Australia/Sydney", value: "Australia/Sydney" },
    ];
  };

  /**
   * Get queues for escalation dropdown
   */
  getQueues = async (): Promise<any[]> => {
    const data = await get("/custom_api/queue/list/");
    // Check key "data" or "results" or direct array
    const list = Array.isArray(data)
      ? data
      : (data?.data ?? data?.results ?? []);

    return list.map((item: any) => ({
      label: item.name,
      value: item.id, // or item.name depending on requirements
    }));
  };

  /**
   * Get Modules list for SLA dropdown
   */
  /** POST /customer-sla/sla-configurations/ — SLA schema mapping (Formily). */
  createCustomerSlaConfiguration = async (
    payload: CustomerSlaConfigurationPayload
  ): Promise<unknown> => {
    return await post(customerSlaConfigurationsPath, payload);
  };

  /** GET /customer-sla/sla-configurations/ */
  listCustomerSlaConfigurations = async (): Promise<unknown> => {
    return await get(customerSlaConfigurationsPath);
  };

  /** GET /customer-sla/sla-configurations/:id/ */
  getCustomerSlaConfigurationById = async (id: string): Promise<unknown> => {
    return await get(`${customerSlaConfigurationsPath}${encodeURIComponent(id)}/`);
  };

  /** PATCH /customer-sla/sla-configurations/:id/ */
  updateCustomerSlaConfiguration = async (
    id: string,
    payload: CustomerSlaConfigurationPayload
  ): Promise<unknown> => {
    return await patch(`${customerSlaConfigurationsPath}${encodeURIComponent(id)}/`, payload);
  };

  /** DELETE /customer-sla/sla-configurations/:id/ */
  deleteCustomerSlaConfiguration = async (id: string): Promise<void> => {
    return await del(`${customerSlaConfigurationsPath}${encodeURIComponent(id)}/`);
  };

  getModules = async (): Promise<any[]> => {
    // URL from curl: /api/sla/modules/
    // Since axiosConfig usually handles baseURL, assuming relative path /sla/modules/
    const data = await get("/sla/modules/");
    if (Array.isArray(data)) {
      return data;
    } else if (data?.results && Array.isArray(data.results)) {
      return data.results;
    } else if (data?.data && Array.isArray(data.data)) {
      return data.data;
    }
    return [];
  };
}

const instance = new SLAConfigurationService();
export default instance;
